import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { hasOptionsHoldings } from '@shared/helpers/margin-and-options.helpers';
import { buildGiftingRecipientValidationPayload } from '@shared/helpers/recipient-validation-helper';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { RecipientValidationService } from '@shared/services/recipient-validation/recipient-validation.service';
import { environment } from '@src/environments';
import { closeSpinner, LogError, LogInfo, openSpinner } from '@store/actions';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';
import { selectMOGiftingToggle } from '@store/selectors/feature/feature.selectors';
import { selectedAccountsFrom } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as RecipientValidationActions from '../../actions/recipient-validation/recipient-validation.actions';
import * as fromRootStore from '../../index';
import { selectRecipientValidationAttemptCount } from '../../selectors/recipient-validation/recipient-validation.selectors';

@Injectable()
export class RecipientValidationEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private recipientValidationService: RecipientValidationService,
  ) {}

  validateMultiITAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientValidationActions.validateMultiITAccounts),
      tap(() => {
        this.store$.dispatch(openSpinner());
      }),
      switchMap(({ accounts }) =>
        this.recipientValidationService.validateMulti(accounts).pipe(
          switchMap((results) => [
            RecipientValidationActions.setMultiITValidationResults({ results }),
            closeSpinner(),
          ]),
          catchError((error: unknown) => [
            LogError({
              payload: {
                eventId: 'recipient-validation-multi-error--validateMultiITAccountsEffect',
                message: `Error validating multi IT accounts: ${error instanceof Error ? error.message : String(error)}`,
                code: LoggerCodes.NULL_FAILURE,
              },
            }),
            RecipientValidationActions.setMultiITValidationResults({ results: [] }),
            closeSpinner(),
          ]),
        ),
      ),
    ),
  );

  validateRecipient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RecipientValidationActions.validateRecipientAccount),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedPositions)),
        this.store$.pipe(select(selectRecipientValidationAttemptCount)),
        this.store$.pipe(select(selectMOGiftingToggle)),
        this.store$.pipe(select(selectedAccountsFrom)),
      ),
      tap(() => {
        this.store$.dispatch(openSpinner());
      }),
      switchMap(
        ([{ accountNumber, ownerName }, positions, attemptCount, isMOToggleOn, accountsFrom]) => {
          // Only apply 3-attempt limit to external users, internal users can try unlimited times
          if (!environment.isInternal && attemptCount >= 3) {
            return [
              LogInfo({
                payload: {
                  eventId: 'recipient-validation-max-attempts--validateRecipientEffect',
                  message: 'Max validation attempts reached. Automatically passing validation.',
                },
              }),
              RecipientValidationActions.setRecipientValidation({
                accountNumber,
                valid: true,
                failureReasons: [],
              }),
            ];
          }
          const [fromAccount] = accountsFrom ?? [];
          const fromAccountNumber =
            isMOToggleOn && fromAccount?.accountNumber && hasOptionsHoldings(positions)
              ? fromAccount.accountNumber
              : undefined;
          return this.recipientValidationService
            .validate(
              buildGiftingRecipientValidationPayload(
                accountNumber,
                ownerName,
                positions,
                fromAccountNumber,
              ),
            )
            .pipe(
              switchMap((result) => {
                const actions: Action[] = [
                  RecipientValidationActions.setRecipientValidation({
                    accountNumber,
                    valid: result.valid,
                    failureReasons: result.failureReasons ?? [],
                  }),
                ];
                if (!result.valid) {
                  actions.push(RecipientValidationActions.incrementAttemptCount());
                  actions.push(closeSpinner());
                }
                return actions;
              }),
              catchError((error: unknown) => [
                LogError({
                  payload: {
                    eventId: 'recipient-validation-error--validateRecipientEffect',
                    message: `Error validating account: ${error instanceof Error ? error.message : String(error)}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
                RecipientValidationActions.setRecipientValidation({
                  accountNumber,
                  valid: true,
                  failureReasons: [],
                }),
              ]),
            );
        },
      ),
    ),
  );
}
